import { api } from '@/lib/axios';
import { getDefaultStore } from 'jotai';
import { authUserMetaAtom, isRehydratingAtom } from '@/store/authAtoms';
import { remoteAccessContextAtom } from '@/store/remoteAccessContextAtom';
import { setCsrfToken } from '@/lib/csrf';

const store = getDefaultStore();

interface SessionStatusResponse {
  userKey: number;
  userName: string;
  session: string;
  iCodeContext: null;
  csrfToken?: string;
}

/**
 * Called once at app init (module level in main.tsx) to verify the BFF session.
 *
 * If `authUserMetaAtom` is present in localStorage, the user was previously logged in.
 * We ping GET /Authentication/SessionStatus to confirm the BFF session cookie is still valid.
 *
 * On success: refreshes `authUserMetaAtom` with the latest server-returned values.
 * On failure (401 / network error): clears `authUserMetaAtom` — ProtectedRoute redirects to /home.
 */
export function bootstrapAuth(): void {
  // Skip on the remote access login page — RemoteAccessBootstrap clears stale auth before
  // redirecting here, and we must not race with the RemoteAccessLogin form submission (a stale
  // SessionStatus 401 resolving after submit would wipe the newly-set auth state).
  if (window.location.pathname === '/remote-access-login') return;

  const meta = store.get(authUserMetaAtom);
  if (!meta) return;

  // Set rehydrating flag synchronously — before any await — so the first React
  // render (which happens after module evaluation) sees isRehydratingAtom = true.
  store.set(isRehydratingAtom, true);

  api
    .get<SessionStatusResponse>('/Authentication/SessionStatus')
    .then(({ data }) => {
      // A remote access session was established while this in-flight request was pending.
      // Don't overwrite the new remote access auth with the old session's data.
      if (store.get(remoteAccessContextAtom) !== null) return;
      if (data.csrfToken) setCsrfToken(data.csrfToken);
      store.set(authUserMetaAtom, {
        id: String(data.userKey),
        fullName: data.session === 'remoteAccess' ? (meta.fullName ?? data.userName) : data.userName,
        roles: meta.roles,
        session: meta.session,
        passwordMustBeReset: meta.passwordMustBeReset
      });
    })
    .catch(() => {
      // Don't clear auth if a remote access session was established while this stale
      // SessionStatus request was in-flight — the 401 is for the old session, not the new one.
      if (store.get(remoteAccessContextAtom) !== null) return;
      store.set(authUserMetaAtom, null);
    })
    .finally(() => {
      store.set(isRehydratingAtom, false);
    });
}
